// src/components/layouts/DashboardLayout.tsx
import { useState, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isPartnerUser } from '@/utils/partnerUtils';
import Sidebar from '@/components/navigation/Sidebar';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { AlertNotifications } from '@/components/ui/AlertNotifications';
import { PersistentAccountStatusBanner } from '@/components/ui/PersistentAccountStatusBanner';
import { WhatsNewBanner } from '@/components/ui/WhatsNewBanner';

// Lazy-load heavy component that's only shown for non-partner users
const AssistantTestDock = lazy(() => import('@/components/ui/AssistantTestDock'));

export default function DashboardLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isTestDockOpen, setIsTestDockOpen] = useState(false);
  const { profile } = useAuth();
  const isPartner = isPartnerUser(profile?.user_type);

  return (
    <div className="flex h-screen bg-muted">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Floating hamburger button for mobile */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsSidebarOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 h-10 w-10 rounded-xl bg-white/90 hover:bg-white shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center backdrop-blur-sm border border-gray-200/50"
      >
        <Menu className="h-5 w-5 text-foreground" />
      </Button>

      <div className="flex-1 flex flex-col overflow-hidden">
        <PersistentAccountStatusBanner />
        <WhatsNewBanner />
        <main className="flex-1 overflow-y-auto bg-background pt-14 px-4 pb-4 md:pt-6 md:px-6 md:pb-6 lg:pt-8 lg:px-8 lg:pb-8">
          <Outlet />
        </main>
      </div>
      <Toaster richColors position="top-right" />
      <AlertNotifications />

      {/* Test AI button - available for regular users and admins (not partners) */}
      {!isPartner && (
        <Suspense fallback={null}>
          <AssistantTestDock
            assistantId={null}
            open={isTestDockOpen}
            onOpenChange={setIsTestDockOpen}
          />
        </Suspense>
      )}
    </div>
  );
}